import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type ExportRecord, type Transcript } from '../api/client.ts';
import { useCancelledOnUnmount } from '../lib/use-cancelled.ts';
import { t } from '../i18n/index.ts';
import { formatDuration, maxFromError } from '../lib/format.ts';
import { giveUpAfterMs } from '../lib/wait.ts';

const POLL_MS = 1000;
/** Only used until the server says how long it expects to take. */
const GIVE_UP_FLOOR_MS = 10 * 60 * 1000;

interface Props {
  timeZone: string;
}

/**
 * The transcription desk.
 *
 * Two ways in, because producers arrive from two directions: a cut they already
 * made and downloaded, or a file from somewhere else entirely -- a phone
 * recording, a clip a correspondent sent. Everything transcribed lands in one
 * list underneath, newest first, so the morning's work stays in one place
 * rather than living inside whichever block editor produced it.
 */
export function TranscribeScreen({ timeZone }: Props) {
  const [exports, setExports] = useState<ExportRecord[] | null>(null);
  const [transcripts, setTranscripts] = useState<Transcript[] | null>(null);
  const [busyExport, setBusyExport] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const cancelled = useCancelledOnUnmount();

  const load = useCallback(async (): Promise<void> => {
    const [list, done] = await Promise.all([
      api.exports().catch(() => [] as ExportRecord[]),
      api.transcripts().catch(() => [] as Transcript[]),
    ]);
    if (cancelled.current) return;
    setExports(list.filter((row) => row.status === 'ready'));
    setTranscripts(done);
  }, [cancelled]);

  useEffect(() => { void load(); }, [load]);

  /** Follow one transcript until it settles, then refresh the list around it. */
  const follow = useCallback(async (created: Transcript): Promise<void> => {
    setTranscripts((prev) => [created, ...(prev ?? []).filter((r) => r.id !== created.id)]);
    setOpenId(created.id);

    let current = created;
    const deadline = Date.now() + giveUpAfterMs(created.expected_sec, GIVE_UP_FLOOR_MS);
    while (
      (current.status === 'queued' || current.status === 'processing')
      && Date.now() < deadline
      && !cancelled.current
    ) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      current = await api.transcript(current.id);
      if (cancelled.current) return;
      setTranscripts((prev) =>
        (prev ?? []).map((row) => (row.id === current.id ? current : row)));
    }
  }, [cancelled]);

  function describe(err: unknown): string {
    if (err instanceof ApiError) {
      const known: Record<string, string> = {
        transcription_unavailable: t('transcript.unavailable'),
        too_long: t('transcript.too_long', { max: maxFromError(err.details) }),
        unsupported_type: t('transcribe.unsupported'),
        file_too_large: t('transcribe.too_large'),
        export_expired: t('transcribe.export_expired'),
        export_not_ready: t('transcribe.export_not_ready'),
      };
      return known[err.code] ?? t('transcript.failed');
    }
    return t('transcript.failed');
  }

  async function fromExport(row: ExportRecord): Promise<void> {
    setBusyExport(row.id);
    setError(null);
    try {
      await follow(await api.transcribeExport(row.id));
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusyExport(null);
    }
  }

  async function fromFile(file: File): Promise<void> {
    setUploading(true);
    setError(null);
    try {
      await follow(await api.transcribeUpload(file));
    } catch (err) {
      setError(describe(err));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  const timeFormat = new Intl.DateTimeFormat('en-GB', {
    timeZone, hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit',
  });

  return (
    <section className="transcribe-screen">
      <h2 className="screen-title">{t('transcribe.title')}</h2>
      <p className="screen-lede">{t('transcribe.lede')}</p>

      {error && <div className="banner error" role="alert">{error}</div>}

      <div className="transcribe-sources">
        {/* Bring in audio from outside the archive. */}
        <div
          className={`dropzone${dragging ? ' over' : ''}${uploading ? ' busy' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const file = e.dataTransfer.files[0];
            if (file && !uploading) void fromFile(file);
          }}
        >
          <span className="dropzone-title">{t('transcribe.upload_title')}</span>
          <span className="dropzone-hint">{t('transcribe.upload_hint')}</span>
          <button
            type="button"
            className="primary"
            disabled={uploading}
            onClick={() => fileInput.current?.click()}
          >
            {uploading ? t('transcribe.uploading') : t('transcribe.choose_file')}
          </button>
          <input
            ref={fileInput}
            type="file"
            hidden
            accept="audio/*,video/mp4,video/quicktime,video/webm"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void fromFile(file);
            }}
          />
        </div>

        {/* Or pick something already cut and downloaded. */}
        <div className="picker">
          <span className="picker-title">{t('transcribe.from_exports')}</span>
          {exports === null && <div className="empty">{t('common.loading')}</div>}
          {exports !== null && exports.length === 0 && (
            <div className="empty">{t('transcribe.no_exports')}</div>
          )}
          {exports !== null && exports.length > 0 && (
            <ul className="picker-list">
              {exports.map((row) => (
                <li key={row.id}>
                  <span className="picker-name">
                    {row.label || t('exports.untitled')}
                  </span>
                  <span className="picker-meta mono">
                    {formatDuration(row.duration_sec)}
                    {' · '}
                    {timeFormat.format(new Date(row.created_at))}
                  </span>
                  <button
                    type="button"
                    disabled={busyExport !== null}
                    onClick={() => void fromExport(row)}
                  >
                    {busyExport === row.id
                      ? t('transcript.working')
                      : t('transcribe.transcribe')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <h3 className="section-title">{t('transcribe.results')}</h3>
      {transcripts !== null && transcripts.length === 0 && (
        <div className="empty">{t('transcribe.nothing_yet')}</div>
      )}
      <ul className="transcript-list">
        {(transcripts ?? []).map((row) => {
          const open = openId === row.id;
          const working = row.status === 'queued' || row.status === 'processing';
          return (
            <li key={row.id} className={`transcript-card${open ? ' open' : ''}`}>
              <button
                type="button"
                className="transcript-head"
                onClick={() => setOpenId(open ? null : row.id)}
              >
                <span className={`pill ${row.status}`}>
                  {working ? t('transcript.working') : t(`transcript.status.${row.status}`)}
                </span>
                <span className="transcript-name">{nameOf(row, timeFormat)}</span>
                <span className="transcript-meta mono">
                  {row.duration_sec !== null && formatDuration(row.duration_sec)}
                  {row.took_ms !== null && ` · ${(row.took_ms / 1000).toFixed(1)}s`}
                </span>
              </button>

              {open && (
                <div className="transcript-body">
                  <p className="transcript-caveat">{t('transcript.caveat')}</p>
                  {row.status === 'failed' && (
                    <p className="editor-hint">{row.error_message ?? t('transcript.failed')}</p>
                  )}
                  {row.status === 'ready' && row.lines.length === 0 && (
                    <p className="editor-hint">{t('transcript.empty')}</p>
                  )}
                  {row.lines.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          void navigator.clipboard?.writeText(row.text ?? '').then(
                            () => {
                              setCopiedId(row.id);
                              setTimeout(() => setCopiedId(null), 2000);
                            },
                            () => undefined,
                          );
                        }}
                      >
                        {copiedId === row.id ? t('transcript.copied') : t('transcript.copy')}
                      </button>
                      <ol className="transcript-lines">
                        {row.lines.map((line, index) => (
                          <li key={index}>
                            <span className="mono line-at">
                              {formatDuration(line.fromMs / 1000)}
                            </span>
                            <span className="line-text">{line.text}</span>
                          </li>
                        ))}
                      </ol>
                    </>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * What to call a transcript in the list. An upload and an export carry their own
 * name; a range picked on a waveform is known only by when it was.
 */
function nameOf(row: Transcript, format: Intl.DateTimeFormat): string {
  if (row.source_name) return row.source_name;
  if (row.range_start_at) return format.format(new Date(row.range_start_at));
  return format.format(new Date(row.created_at));
}
